import React from 'react';

import { ChartContainer } from '@mui/x-charts/ChartContainer';
import { ChartsGrid } from '@mui/x-charts/ChartsGrid';
import {
    AreaPlot,
    LinePlot,
    areaElementClasses,
} from '@mui/x-charts/LineChart';
import { ChartsYAxis } from '@mui/x-charts/ChartsYAxis';
import { ChartsXAxis } from '@mui/x-charts/ChartsXAxis';

import { AxisId } from 'ff/debrief/models';
import CustomTooltip from 'ff/debrief/components/charts/tooltip/CustomTooltip';
import {
    createSeriesConfigure,
    getTimeSeriesAxisConfig,
    getYAxisConfig,
} from 'ff/debrief/components/charts/utils';

import {
    BlueLinearGradient,
    PinkLinearGradient,
} from 'ff/debrief/components/charts/defs';
import { axisId } from 'ff/debrief/utils';
import RightYAxisWrapper from 'ff/debrief/components/charts/axis/RightYAxisWrapper';
import Scrubber from 'ff/debrief/components/charts/Scrubber';
import ScrubberWrapper from './utils/ScrubberWrapper';

interface Props {
    timestamps?: number[];
    leftAxisId?: AxisId;
    rightAxisId?: AxisId;
    leftAxisData?: number[];
    rightAxisData?: number[];
    terrainData?: number[];
    tooltipBounds?: React.RefObject<HTMLDivElement>;
    speedUnit?: string;
    children?: React.ReactNode;
}

const GRADIENT_ID = 'altitude-gradient';
const AGL_GRADIENT_ID = 'agl-gradient';

const FlightChart = React.memo<Props>(function FlightChart({
    timestamps,
    leftAxisId,
    rightAxisId,
    leftAxisData,
    rightAxisData,
    tooltipBounds,
    terrainData,
    speedUnit,
    children,
}: React.PropsWithChildren<Props>) {
    const altitudeAxes: AxisId[] = [
        axisId.AltitudeAxis,
        axisId.AboveGroundLevelAxis,
    ];
    const isPlottingTerrain =
        leftAxisId === axisId.AltitudeAxis ||
        rightAxisId === axisId.AltitudeAxis;
    const useSharedYAxis =
        altitudeAxes.includes(leftAxisId) && altitudeAxes.includes(rightAxisId);

    const leftYAxisConfig = {
        ...getYAxisConfig(leftAxisId, speedUnit)(leftAxisData),
        position: 'left' as const,
    };
    const rightYAxisConfig = {
        ...getYAxisConfig(rightAxisId, speedUnit)(rightAxisData),
        position: 'right' as const,
    };

    const leftSeriesConfig = createSeriesConfigure(
        leftAxisId,
        speedUnit
    )(leftAxisData);
    const rightSeriesConfig = createSeriesConfigure(
        rightAxisId,
        speedUnit
    )(rightAxisData);

    const yAxesConfig = useSharedYAxis
        ? [leftYAxisConfig, { id: rightAxisId }] // dummy placeholder
        : [leftYAxisConfig, rightYAxisConfig];

    const seriesConfig = useSharedYAxis
        ? [leftSeriesConfig, { ...rightSeriesConfig, yAxisId: leftAxisId }]
        : [leftSeriesConfig, rightSeriesConfig];

    if (isPlottingTerrain && useSharedYAxis) {
        seriesConfig.push({
            ...createSeriesConfigure(
                axisId.TerrainElevationAxis,
                speedUnit
            )(terrainData),
            yAxisId: leftAxisId,
        });
    } else if (isPlottingTerrain) {
        seriesConfig.push(
            createSeriesConfigure(
                axisId.TerrainElevationAxis,
                speedUnit
            )(terrainData)
        );
    }

    return (
        <ChartContainer
            xAxis={[getTimeSeriesAxisConfig(timestamps)]}
            yAxis={yAxesConfig}
            series={seriesConfig}
            sx={{
                userSelect: 'none',
                [`& .${areaElementClasses.root}`]: {
                    [`&.MuiAreaElement-series-${axisId.AltitudeAxis}`]: {
                        fill: `url(#${GRADIENT_ID})`,
                    },
                    [`&.MuiAreaElement-series-${axisId.AboveGroundLevelAxis}`]:
                        {
                            fill: `url(#${AGL_GRADIENT_ID})`,
                        },
                    [`&.MuiAreaElement-series-${axisId.TerrainElevationAxis}`]:
                        {
                            fill: '#BA7B197f',
                        },
                },
                [`& .MuiLineElement-series-${axisId.TerrainElevationAxis}`]: {
                    stroke: 'transparent',
                },
                '& .MuiChartsGrid-line': {
                    strokeDasharray: '4 6',
                },
                '& .MuiChartsAxis-root.MuiChartsAxis-left': {
                    transform: 'translate(15px, -10px)',
                },
                '& .MuiChartsAxis-root.MuiChartsAxis-right': {
                    translate: '0px -10px',
                },
            }}
            margin={{ left: -80, right: -80 }}
        >
            <defs>
                <BlueLinearGradient id={GRADIENT_ID} />
                <PinkLinearGradient id={AGL_GRADIENT_ID} />
            </defs>
            <ChartsGrid horizontal />
            <ChartsYAxis
                tickLabelStyle={{
                    textAnchor: 'start',
                }}
                disableLine
                disableTicks
            />
            {/* Because of a bug in MUI X we need to wrap the right axis */}
            <RightYAxisWrapper rightAxisId={rightAxisId} />
            <ChartsXAxis
                tickLabelStyle={{ transform: 'translateY(16px)' }}
                disableLine
                disableTicks
            />
            <AreaPlot />
            <LinePlot />
            <CustomTooltip boundaryEl={tooltipBounds} />
            <ScrubberWrapper axisId={useSharedYAxis ? 'altitude' : leftAxisId}>
                <Scrubber
                    axis={useSharedYAxis ? 'altitude' : leftAxisId}
                    withAirplane={
                        leftAxisId === 'altitude' || rightAxisId === 'altitude'
                    }
                />
            </ScrubberWrapper>
            {children}
        </ChartContainer>
    );
});

export default FlightChart;
